/**
 * The slice of the Telegram Mini App SDK this panel actually uses.
 *
 * Typed by hand rather than pulled from a package: it is a handful of fields, and the SDK is injected
 * by Telegram at runtime, so a dependency would only add a version to keep in step.
 */
export interface TelegramWebApp {
  initData: string
  colorScheme: 'light' | 'dark'
  themeParams: Record<string, string>
  ready(): void
  expand(): void
  openTelegramLink(url: string): void
  openLink?(url: string): void
  /** The SDK is loaded in any browser, but outside a Telegram client its methods past 6.0 throw. */
  isVersionAtLeast?(version: string): boolean
  /** Opens a Stars invoice made with createInvoiceLink; the status is only a hint (the bot credits). */
  openInvoice?(url: string, callback?: (status: 'paid' | 'cancelled' | 'failed' | 'pending') => void): void
  showConfirm?(message: string, callback?: (ok: boolean) => void): void
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy'): void
    notificationOccurred(type: 'error' | 'success' | 'warning'): void
  }
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp }
  }
}

export const webApp = (): TelegramWebApp | undefined => window.Telegram?.WebApp

/**
 * The initData to log in with.
 *
 * Inside Telegram it comes from the SDK. Outside it there is none, so in a dev build a value from
 * `.env.local` is used instead. The `import.meta.env.DEV` guard is what keeps that out of a
 * production bundle — Vite replaces it with `false` and the branch is dropped.
 */
export function getInitData(): string | null {
  const fromTelegram = webApp()?.initData
  if (fromTelegram) return fromTelegram

  if (import.meta.env.DEV) {
    const fromEnv = import.meta.env.VITE_DEV_INIT_DATA
    if (fromEnv) return fromEnv
  }

  return null
}

/** Applies Telegram's theme so the panel matches the client it is embedded in. */
export function applyTheme(): void {
  const app = webApp()
  if (!app) return

  const root = document.documentElement
  for (const [key, value] of Object.entries(app.themeParams)) {
    root.style.setProperty(`--tg-${key.replace(/_/g, '-')}`, value)
  }
  root.dataset.scheme = app.colorScheme
}

/**
 * Asks before something that cannot be undone from here (disconnecting, stopping the auto-renewal), in
 * Telegram's own dialog where there is one.
 */
export function confirmAction(message: string): Promise<boolean> {
  const app = supporting('6.2')
  if (app?.showConfirm) {
    return new Promise((resolve) => {
      try {
        app.showConfirm!(message, (ok) => resolve(ok))
      } catch {
        resolve(window.confirm(message))
      }
    })
  }
  return Promise.resolve(window.confirm(message))
}

/**
 * Opens an invoice and resolves with what the client reports. Never proof of payment: the licence moves
 * only when the bot receives Telegram's successful_payment, so a "paid" here means "look again soon".
 */
export function payInvoice(link: string): Promise<'paid' | 'cancelled' | 'failed' | 'pending'> {
  const app = supporting('6.1')
  if (app?.openInvoice) {
    return new Promise((resolve) => {
      try {
        app.openInvoice!(link, (status) => resolve(status))
      } catch {
        window.open(link, '_blank', 'noopener')
        resolve('pending')
      }
    })
  }
  // Outside Telegram (a browser during development) the link opens Telegram, which takes it from there.
  window.open(link, '_blank', 'noopener')
  return Promise.resolve('pending')
}

/** Opens a link: a t.me link inside Telegram, anything else in the browser. */
export function openExternal(url: string): void {
  const app = supporting('6.1')
  try {
    if (app && url.startsWith('https://t.me/')) return app.openTelegramLink(url)
    if (app?.openLink) return app.openLink(url)
  } catch {
    /* an older client: the browser below */
  }
  window.open(url, '_blank', 'noopener')
}

/**
 * The SDK when the client running it is at least this version. The script is loaded in any browser,
 * where it reports 6.0 and throws from every newer method — so "the method exists" is not enough.
 */
function supporting(version: string): TelegramWebApp | undefined {
  const app = webApp()
  return app && app.initData && app.isVersionAtLeast?.(version) ? app : undefined
}

/** Light haptic feedback where the client supports it; silently nothing where it does not. */
export function haptic(kind: 'tap' | 'ok' | 'error'): void {
  const h = webApp()?.HapticFeedback
  if (!h) return
  if (kind === 'tap') h.impactOccurred('light')
  else h.notificationOccurred(kind === 'ok' ? 'success' : 'error')
}
